import { AfterViewInit, Component, Input, OnChanges, OnInit, ViewChild, ChangeDetectorRef } from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ChartDataset, ChartType } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

export interface SummaryData {
  key: string;
  elements: StandardInvoicesStatisticsData[];
  invoiceCount: number;
  value: number;
}

export interface StandardInvoicesStatisticsData {
  invoiceId: string;
  productId: string;
  invoiceRowTotalValueCurrency: number;
  invoiceRowTotalQuantity: number;
}

@Component({
    selector: 'lib-invoices-analytics-chart-table',
    templateUrl: './invoices-analytics-chart-table.component.html',
    styleUrls: ['./invoices-analytics-chart-table.component.css'],
    standalone: false
})
export class InvoicesAnalyticsChartTableComponent implements OnInit, OnChanges, AfterViewInit {
  @Input()
  data: StandardInvoicesStatisticsData[];

  @Input()
  groupBy: string;

  @Input()
  tableOnly: boolean;

  @Input()
  heading: string;

  barChartData: ChartDataset[];
  barChartLabels: BaseChartDirective['labels'] = [];
  barChartOptions: any = {}; // todo
  barChartLegend = false;
  barChartType: ChartType = 'bar';

  summaryData: SummaryData[];
  summaryTotals;

  @ViewChild(MatSort) sort: MatSort;
  displayedColumns = [];

  dataSource = new MatTableDataSource();
  searchData: Subject<Event> = new Subject();

  loading = true;

  constructor(protected changeDetectorRef: ChangeDetectorRef) {}

  ngOnInit() {
    this.barChartOptions = {
      backgroundColor: 'rgba(206,212,218, 0.6)',
      hoverBackgroundColor: 'rgba(206,212,218, 0.8)'
    };
    this.searchData.pipe(debounceTime(100)).subscribe(search => {
      // Need to cast it
      const element = search.target as HTMLInputElement;
      const searchTerm = element.value;
      this.dataSource.data = this.summaryData.filter(item => {
        // If MultiDimension
        if (!!item.elements[0].productId) {
          return (
            item.key.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.elements[0].productId.includes(searchTerm)
          );
        } else {
          // If not MultiDimension
          return (
            item.key.toLowerCase().includes(searchTerm.toLowerCase()) || item.elements[0].productId.includes(searchTerm)
          );
        }
      });
    });
  }

  ngOnChanges() {
    const groupedData = this.groupData();
    const groupedDataValue = groupedData.map(element => {
      element.value = Math.round(
        element.elements.reduce((acc: number, curr: StandardInvoicesStatisticsData) => {
          return acc + curr.invoiceRowTotalValueCurrency;
        }, 0)
      );
      element.count = Math.round(
        element.elements.reduce((acc: number, curr: StandardInvoicesStatisticsData) => {
          return acc + curr.invoiceRowTotalQuantity;
        }, 0)
      );
      return element;
    });

    const chartData = [];
    const chartLabels = [];
    groupedDataValue.forEach(element => {
      chartData.push(element.value);
      chartLabels.push(element.key);
    });
    this.barChartData = [
      {
        data: chartData
      }
    ];
    this.barChartLabels = chartLabels;

    if (this.heading === 'INVOICES') {
      this.displayedColumns = ['key', 'value', 'count'];
    } else if (this.heading === 'PRODUCTS') {
      this.displayedColumns = ['productId', 'key', 'value', 'count', 'invoiceCount'];
    } else {
      this.displayedColumns = ['key', 'value', 'count', 'invoiceCount'];
    }

    this.buildSummaryData(this.groupBy === 'productName' ? groupedDataValue : groupedDataValue.reverse());

    this.loading = false;
  }

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
    this.sort.sort({ id: 'value', start: 'desc', disableClear: true });
    this.changeDetectorRef.detectChanges();
  }

  protected groupData(): { [key: string]: StandardInvoicesStatisticsData[] | any }[] {
    const result = [];
    const groupedData = this.data.reduce((acc, curr) => {
      if (this.groupBy === 'invoiceCreationDateYearMonth') {
        const quarterKey = 'invoiceCreationDateYearMonthQuarter';
        const year = curr[quarterKey].split('-')[0];
        (acc[year] = acc[year] || []).push(curr);
      } else {
        (acc[curr[this.groupBy]] = acc[curr[this.groupBy]] || []).push(curr);
      }
      return acc;
    }, {});
    for (const key of Object.keys(groupedData)) {
      result.push({
        key,
        elements: groupedData[key]
      });
    }

    result.sort((p1, p2) => {
      const key1: string = p1.key;
      const key2: string = p2.key;
      return key1.localeCompare(key2);
    });

    return result;
  }

  protected buildSummaryData(grouped: { [key: string]: any }) {
    const summaryTotals = {
      value: 0,
      count: 0,
      invoiceCount: 0
    };

    this.summaryData = grouped.map(element => {
      const invoiceCount = new Set();
      element.elements.forEach((e: StandardInvoicesStatisticsData) => {
        invoiceCount.add(e.invoiceId);
      });
      element.invoiceCount = invoiceCount.size;

      summaryTotals.value += element.value;
      summaryTotals.count += element.count;
      summaryTotals.invoiceCount += element.invoiceCount;

      return element;
    });

    this.summaryTotals = summaryTotals;
    this.dataSource.data = this.summaryData;
  }
}
